import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import type { AuthResponse } from "@aika/types";
import { getUnixTime } from "date-fns";

export const userKey = "aika-user";

export type LoginPayload =
  | {
      email: string;
      password: string;
    }
  | { nonce: string };

export type SignupPayload = {
  firstName: string;
  lastName: string | null;
  email: string;
  password: string;
  allowEmails: boolean;
};

export type UserContextType = {
  user: AuthResponse | null;
  login: (loginPayload: LoginPayload) => Promise<boolean>;
  logout: () => Promise<void>;
  signup: (signupPayload: SignupPayload) => Promise<boolean>;
  resetPassword: (code: string, password: string) => Promise<boolean>;
  confirmEmail: (code: string) => Promise<boolean>;
};

export const UserContext = createContext<UserContextType>(null!);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<AuthResponse | null>(null);

  useEffect(() => {
    // Check for old token auth
    let existingUser = JSON.parse(window.localStorage.getItem(userKey));

    let headers: any = {};

    if (existingUser != null) {
      headers.Authorization = "Bearer " + existingUser.token;
    }

    axios
      .get("/api/auth/me", { headers })
      .then(({ data }) => {
        setUser(data);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        window.localStorage.removeItem(userKey);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const i = axios.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        if (error.response?.status === 401) {
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(i);
    };
  }, []);

  const login = useCallback(async (login: LoginPayload): Promise<boolean> => {
    try {
      const { data } = await axios.post("/api/auth/token", login);
      setUser(data);
      return true;
    } catch (e) {
      setUser(null);
      return false;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await axios.post("/api/auth/logout");
    setUser(null);
  }, []);

  const resetPassword = useCallback(
    async (code: string, password: string): Promise<boolean> => {
      try {
        const { data } = await axios.post("/api/auth/reset-password", {
          code,
          password,
        });
        setUser(data);
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  const confirmEmail = useCallback(
    async (code: string) => {
      try {
        const res = await axios.post<{ userId: string }>(
          `/api/auth/confirm-email/${code}`
        );

        if (user?.id === res.data.userId) {
          // We just confirmed the logged in user.
          // Should be the case almost all the time but check just in case.
          setUser({
            ...user,
            emailConfirmed: getUnixTime(new Date()),
          });
        }
        return true;
      } catch {
        return false;
      }
    },
    [user]
  );

  const signup = useCallback(
    async (payload: SignupPayload): Promise<boolean> => {
      try {
        const { data } = await axios.post("/api/auth/signup", payload);
        setUser(data);
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  const value = {
    user,
    login,
    logout,
    signup,
    resetPassword,
    confirmEmail,
  };

  return (
    <UserContext.Provider value={value}>
      {loading ? null : children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
